import React from 'react'
import type { QueryResult, MutationResult } from '@apollo/client/react'
import { Error, Loading } from '../../components/responses'
import {
  setUnauthorizedNotification,
  setStatusChangeForbiddenNotification,
  setGoogleAPIGeolocationErrorNotification,
  setResourceNotFoundNotification,
} from '../notification'
import { dataHasError } from './dataHasError'
import { NetworkStatus } from '@apollo/client'

interface OperationVariables {
  [key: string]: any
}

interface Props<TData = any, TVariables extends OperationVariables = OperationVariables> {
  children: (response?: TData) => React.ReactNode
  queryResult?: QueryResult<TData, TVariables>
  mutationResult?: MutationResult<TData>
  loading?: boolean
  notFound?: boolean
  statusChangeForbidden?: boolean
  unauthorized?: boolean
  googleAPIGeolocationError?: boolean
  ignoreLoading?: boolean
}

export type GenericResultHandlerDefinition<TData = any> = React.FC<Props<TData>>

export const GenericResultHandler: GenericResultHandlerDefinition = ({
  children,
  queryResult,
  mutationResult,
  googleAPIGeolocationError = false,
  loading = false,
  notFound = false,
  statusChangeForbidden = false,
  unauthorized = false,
  ignoreLoading = false,
}) => {
  const result = queryResult ? queryResult : mutationResult ? mutationResult : null

  if (result == null) {
    return <Error />
  }

  // useQuery.refetch() signals refetch in progress with networkStatus === 4
  if (queryResult && !ignoreLoading) {
    if (queryResult.networkStatus === NetworkStatus.refetch) return <Loading />
  }

  if (result.error) {
    return <Error error={result.error} />
  }

  if (loading && result.loading && queryResult && queryResult.networkStatus === NetworkStatus.loading) {
    return <Loading />
  }

  if (result.data && unauthorized && dataHasError(result.data, 'UnauthorizedError')) {
    setUnauthorizedNotification()
    return <></>
  }

  if (result.data && notFound && dataHasError(result.data, 'ResourceNotFoundError')) {
    setResourceNotFoundNotification()
    return <></>
  }

  if (result.data && statusChangeForbidden && dataHasError(result.data, 'StatusChangeForbidden')) {
    setStatusChangeForbiddenNotification(result.data.error.message)
    return <></>
  }

  if (result.data && googleAPIGeolocationError && dataHasError(result.data, 'GoogleAPIGeolocationError')) {
    setGoogleAPIGeolocationErrorNotification()
    return <></>
  }

  return <>{children(result.data)}</>
}

// const Test = styled.div<{loading?: boolean}>`
//   transition: opacity 0.2s ease-in-out;
// `
//opacity: ${props => (props.loading ? '0.8' : '1')};